import { useSuspenseQuery } from '@tanstack/react-query';
import type { AdsHistoryGroupedEntry } from '../components/AdsHistoryGroupedView.mockData';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import type { ListResponse, MarketingProcess } from 'types/shared';
import { apiClient } from 'lib/api';

interface AdsHistoryHypothesisRepresentative {
  created_by: string;
  created_at: string;
  ad_account: { name: string; id: string };
  adset: { name: string; id: string };
  message: string;
  status: boolean;
}

interface AdsHistoryHypothesisAd extends Omit<AdsHistoryGroupedEntry, 'pack_name' | 'hypothesis' | 'email' | 'ad_set' | 'created_at'> {
  ad_id: string;
}

interface AdsHistoryHypothesisPackGroup {
  pack_name: string;
  representative: AdsHistoryHypothesisRepresentative;
  ads: AdsHistoryHypothesisAd[];
  total_ads: number;
}

interface AdsHistoryHypothesisGroup {
  hypothesis: string;
  total_ads: number;
  packs: AdsHistoryHypothesisPackGroup[];
}

interface AdsHistoryHypothesisWeek {
  week_key: string;
  label: string;
  groups: AdsHistoryHypothesisGroup[];
}

export type AdsHistoryHypothesisResponse = ListResponse<AdsHistoryHypothesisWeek>;

interface GetAdsHistoryHypothesisParams {
  marketing_process?: MarketingProcess;
  limit?: number;
  offset?: number;
}

export const getAdsHistoryHypothesis = async (
  params?: GetAdsHistoryHypothesisParams,
  signal?: AbortSignal,
): Promise<AdsHistoryHypothesisResponse> => {
  const res = await apiClient.get<AdsHistoryHypothesisResponse>(queryKeys.adsHistoryHypothesis, {
    signal,
    params,
  });

  return res.data;
};

interface UseAdsHistoryHypothesisOptions<TData> {
  config?: QueryConfig<typeof getAdsHistoryHypothesis, TData>;
  params?: GetAdsHistoryHypothesisParams;
}

export const useAdsHistoryHypothesis = <TData = AdsHistoryHypothesisResponse>(
  { config, params }: UseAdsHistoryHypothesisOptions<TData> = {},
) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getAdsHistoryHypothesis(params, signal),
    queryKey: [queryKeys.adsHistoryHypothesis, params?.marketing_process, params?.limit, params?.offset],
  });
};
